import type { Department, Facility } from "../contexts/AuthProvider";
import { compareDepartments } from "./departmentOrder";

/** What may follow a facility's name for it to count as the department name's prefix — never a letter, so "Highlands" keeps its "s". */
const FACILITY_PREFIX_BOUNDARY = /^[\s\-–—:,(]/;
const LEADING_SEPARATORS = /^[\s\-–—:,]+/;
const WRAPPED_IN_PARENTHESES = /^\((.+)\)$/;
const NETWORK_SEPARATOR = ", ";

export type FacilityGroup = {
	facility: Facility;
	/** The network(s) of the facility's departments — one in practice; a facility spanning two lists both. */
	network: string;
	departments: Department[];
};

export type SwitcherRow =
	| { kind: "facility"; group: FacilityGroup; isExpanded: boolean }
	| { kind: "department"; department: Department; group: FacilityGroup; isInsideFacility: boolean };

/**
 * A department's name under its facility, with the facility's name taken off the front: under Center at
 * Lowry, "Center at Lowry - Dietary" reads "Dietary" and "St. Andrew (housekeeping)" reads "housekeeping".
 * A name that doesn't start with the facility's name, or is nothing but it, is shown in full.
 */
export const departmentNameInFacility = (departmentName: string, facilityName: string): string => {
	const fullName = departmentName.trim();
	const prefix = facilityName.trim();
	if (fullName.slice(0, prefix.length).toLocaleLowerCase() !== prefix.toLocaleLowerCase()) return fullName;

	const rest = fullName.slice(prefix.length);
	if (!FACILITY_PREFIX_BOUNDARY.test(rest)) return fullName;

	const shortName = rest.replace(LEADING_SEPARATORS, "").replace(WRAPPED_IN_PARENTHESES, "$1").trim();
	// A name that is the facility's plus only punctuation has nothing left to show on its own.
	if (!shortName) return fullName;
	return shortName;
};

/** The user's departments under their facilities, ordered by network, then facility name, then department as the switcher always did. */
export const groupDepartmentsByFacility = (departments: Department[], facilities: Facility[]): FacilityGroup[] => {
	const facilityById = new Map(facilities.map((facility) => [facility.facility_id, facility]));
	const departmentsByFacility = new Map<Facility, Department[]>();
	for (const department of departments.slice().sort(compareDepartments)) {
		const facility = facilityById.get(department.facility_id);
		if (!facility) {
			// user_info lists the facility of every department it returns, so a miss is a backend bug.
			throw new Error(`Department ${department.department_id} is in facility ${department.facility_id}, which user_info.facilities doesn't list`);
		}
		const facilityDepartments = departmentsByFacility.get(facility);
		if (facilityDepartments) {
			facilityDepartments.push(department);
		} else {
			departmentsByFacility.set(facility, [department]);
		}
	}

	return [...departmentsByFacility.entries()]
		.map(([facility, facilityDepartments]) => ({
			facility,
			network: [...new Set(facilityDepartments.map((department) => department.hospital))].sort().join(NETWORK_SEPARATOR),
			departments: facilityDepartments,
		}))
		.sort((group, otherGroup) => group.network.localeCompare(otherGroup.network) || group.facility.name.localeCompare(otherGroup.facility.name));
};

const includesTerm = (text: string, term: string): boolean => text.toLowerCase().includes(term);

const departmentMatches = (department: Department, group: FacilityGroup, term: string): boolean =>
	includesTerm(department.display_name, term) ||
	includesTerm(department.department_id, term) ||
	includesTerm(departmentNameInFacility(department.display_name, group.facility.name), term);

/**
 * The switcher's rows. A facility with one of the user's departments is that department's row, picked in
 * one click. A facility with several is a row that expands to them; a search expands every facility it
 * matches, showing all of a facility whose own name (or network) matches and only the matching departments otherwise.
 */
export const buildSwitcherRows = (groups: FacilityGroup[], expandedFacilityIds: ReadonlySet<string>, query: string): SwitcherRow[] => {
	const term = query.trim().toLowerCase();
	return groups.flatMap((group): SwitcherRow[] => {
		const facilityMatches = !term || includesTerm(group.facility.name, term) || includesTerm(group.network, term);
		const shownDepartments = group.departments.filter((department) => facilityMatches || departmentMatches(department, group, term));
		if (!shownDepartments.length) return [];
		if (group.departments.length === 1) {
			return [{ kind: "department", department: group.departments[0], group, isInsideFacility: false }];
		}

		const isExpanded = Boolean(term) || expandedFacilityIds.has(group.facility.facility_id);
		const facilityRow: SwitcherRow = { kind: "facility", group, isExpanded };
		if (!isExpanded) return [facilityRow];
		return [facilityRow, ...shownDepartments.map((department): SwitcherRow => ({ kind: "department", department, group, isInsideFacility: true }))];
	});
};

/** What a row reads: the facility's name, a department's short name under its facility, or a lone department's full name. */
export const switcherRowLabel = (row: SwitcherRow): string => {
	if (row.kind === "facility") return row.group.facility.name;
	if (row.isInsideFacility) return departmentNameInFacility(row.department.display_name, row.group.facility.name);
	return row.department.display_name;
};
